import { ReactNode } from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface FormSaveActionsProps {
    /** Where this copy sits: 'header' beside the scenario name, 'footer' after the content, 'sticky' in a
     *  bar pinned to the bottom of the viewport (the Budget grid pages, CALL-779). It decides the
     *  data-testid suffix, the alignment, and — following from the alignment — the order the three items
     *  are laid out in. */
    variant: 'header' | 'footer' | 'sticky';
    canSave: boolean;
    isDirty: boolean;
    isSaving: boolean;
    onSave: () => void;
    onCancel: () => void;
    /** The first blocking problem, so a disabled Save is never a mystery: spelled out beside the footer
     *  and sticky pairs, and carried by the header pair's Save as a tooltip. */
    firstIssue?: string;
    onGoToIssue?: () => void;
    /** Discard just the edits the blocking message is about. The grid pages need an escape that is not
     *  all-or-nothing: the offending row can be behind a filter, so it cannot always be re-edited, and
     *  Verwerfen would take every other pending edit on the page with it (CALL-779). */
    onDiscardIssue?: () => void;
    /** How many pending changes Save would write. Named on the button when given (CALL-779) — the grid
     *  pages need it because their changes are scattered over rows and months that do not all fit on
     *  screen, so the count is the only thing that says how much is at stake. */
    pendingCount?: number;
    /** What the cell colours on this page mean (CellLegend, CALL-779). Sticky variant only, and dropped
     *  while `firstIssue` is showing: both live in the same empty middle of the bar, and a blocking
     *  message must not have to compete with a colour key. */
    legend?: ReactNode;
}

// One Save/Cancel pair, shared by the scenario form (CALL-783) and the Budget grid pages (CALL-779).
//
// The scenario page renders it twice — once in the header, where the metadata is, and once after the
// content, so a user who has scrolled down a month grid does not have to scroll back up to commit. Both
// drive the same form, so both carry the same labels — hence the per-variant data-testid, which is also
// how the tests tell them apart.
//
// The scenario footer used to be a bar PINNED to the bottom of the viewport, and that was removed
// (CALL-784): on a wide screen a floating bar does not read as part of anything, so it was not obvious
// where to click, and being `position: fixed` it had to be paid for with a spacer reserving its height.
// The `sticky` variant is not that bar. It is `position: sticky`, so it is an ordinary block in the page
// flow that happens to pin itself while its page is on screen — no spacer, no clearance arithmetic. It
// exists because the Budget grid pages are the case the scenario page is not: Prices & ABT stacks three
// auto-height grids over the same rows, so a pair after the content can be thousands of pixels from the
// cell being edited. The z-index only has to clear the AppBar; ag-grid popups already sit above both,
// via the global `body .ag-popup-child` rule in App.css.
const FormSaveActions = ({
    variant,
    canSave,
    isDirty,
    isSaving,
    onSave,
    onCancel,
    firstIssue,
    onGoToIssue,
    onDiscardIssue,
    pendingCount,
    legend,
}: FormSaveActionsProps) => {
    const { t } = useTranslation('budget');
    const isHeader = variant === 'header';
    const isSticky = variant === 'sticky';
    // Which page edge this copy hugs, which then decides the order of the three items (see the rule
    // below). The sticky bar sits on the grid pages, where every editable column — the month cells, the
    // numeric config columns — is on the RIGHT of the row, so that is where the hand and the eye already
    // are when the user is done typing.
    const alignRight = isHeader || isSticky;
    // The scenario variants are one form's actions; the sticky bar belongs to a page of grids. Only the
    // testid prefix follows from that, so it is derived rather than passed — the scenario suites query
    // `scenario-save-header` and there is no reason for them to move.
    const idPrefix = isSticky ? 'budget' : 'scenario';

    const cancelButton = (
        <Button
            data-testid={`${idPrefix}-cancel-${variant}`}
            variant="outlined"
            size="small"
            disabled={!isDirty || isSaving}
            onClick={onCancel}
        >
            <span>{isSticky ? t('action.discardChanges') : t('action.cancel')}</span>
        </Button>
    );

    const saveButton = (
        <Button
            data-testid={`${idPrefix}-save-${variant}`}
            variant="contained"
            size="small"
            disabled={!canSave}
            onClick={onSave}
        >
            {/* The count updates in place as the user edits, so it is data rather than copy: a browser
                translation extension rewriting these text nodes is what crashes React (see the
                TranslationErrorBoundary note in CLAUDE.md), hence notranslate on the counted label. */}
            {isSaving ? (
                <span>{t('action.saving')}</span>
            ) : pendingCount ? (
                <span className="notranslate">{t('action.saveChangesCount', { count: pendingCount })}</span>
            ) : (
                <span>{t('action.save')}</span>
            )}
        </Button>
    );

    // The header's Save says on hover what the footer says in words (CALL-794). The header used to carry the
    // message and the jump link too, which put both on screen TWICE and wrapped its row onto a second line
    // tight under the scenario title — the title row already holds the name, the type chip and the unsaved
    // badge. The hover keeps the half that matters: a user at the top of a long month grid still gets told
    // why SPEICHERN is grey, without a second copy of the line the footer already prints beside the flagged
    // grid. The <span> is load-bearing — a DISABLED MUI button emits no hover events, so a Tooltip placed on
    // the button itself would silently never open, which is the whole case this exists for. An empty title
    // never opens, so the wrapper is unconditional and the button is not remounted as the draft goes in and
    // out of a valid state.
    const headerSaveButton = (
        <Tooltip title={firstIssue ?? ''}>
            <span>{saveButton}</span>
        </Tooltip>
    );

    const issue = firstIssue ? (
        <>
            {/* The message changes as the user fixes rows — block translation so the extension cannot
                rewrite a text node React is about to update. */}
            <Typography variant="caption" color="error.main">
                <span className="notranslate">{firstIssue}</span>
            </Typography>
            {onGoToIssue && (
                <Button variant="text" size="small" onClick={onGoToIssue}>
                    <span>{t('scenarioDetail.goToError')}</span>
                </Button>
            )}
            {onDiscardIssue && (
                <Button variant="text" size="small" color="error" onClick={onDiscardIssue}>
                    <span>{t('action.discardIssueRow')}</span>
                </Button>
            )}
        </>
    ) : null;

    // The copies are MIRRORS of each other, and one rule produces them all (CALL-783): the pair sits at the
    // page edge its row is aligned to, Save on the outside, the validation message on the inside.
    //
    //   header, right-aligned:  [ message ] [ Abbrechen ] [ SPEICHERN ] |page edge
    //   footer, left-aligned:   |page edge [ SPEICHERN ] [ Abbrechen ] [ message ]
    //   sticky, right-aligned:  [ message ] [ VERWERFEN ] [ SPEICHERN ] |page edge
    //
    // Save at the outer edge is the half that makes the copies feel like one control in two places — the
    // primary action is always the button closest to the margin, so it is found by position rather than by
    // reading.
    //
    // The message on the INNER side is the half that keeps the buttons still. It appears, changes and vanishes
    // as the user fixes rows, and anything between it and the edge would be pushed sideways every time — the
    // user would be aiming at a SPEICHERN that moves while they work. Outside-in ordering pins both buttons to
    // the margin and lets the message grow into the empty middle of the row instead.
    //
    // The order is expressed by ORDERING THE ELEMENTS, never by `flexDirection: 'row-reverse'` or per-button
    // `order`. Those two move only what is PAINTED: the DOM would keep one order while the screen showed the
    // other, so the keyboard would reach SPEICHERN where ABBRECHEN was drawn — a trap for exactly the users
    // who cannot see the discrepancy. Tab order, screen-reader order and paint order stay in step this way,
    // which is what the `orders each pair outside-in from its own page edge` test asserts.
    const pair = (
        <Stack
            data-testid={`${idPrefix}-actions-${variant}`}
            direction="row"
            alignItems="center"
            spacing={1}
            useFlexGap
        >
            {alignRight ? cancelButton : saveButton}
            {alignRight ? (isHeader ? headerSaveButton : saveButton) : cancelButton}
        </Stack>
    );

    const actions = (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            {/* Only the header hides its message (it hovers on Save instead), so there the pair is all
                there is to place. */}
            {alignRight ? (
                <>
                    {!isHeader && issue}
                    {pair}
                </>
            ) : (
                <>
                    {pair}
                    {issue}
                </>
            )}
        </Stack>
    );

    // The header row already pushes its children to the right edge (a flexGrow spacer before them).
    if (isHeader) return actions;

    if (isSticky) {
        return (
            <Box
                // Named apart from the button pair inside it (`budget-actions-sticky`), the way the
                // footer names its row: the alignment is a property of the BAR, and Testing Library
                // forbids reaching it by walking up from the pair (no-node-access).
                data-testid="budget-actions-sticky-bar"
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    // Opaque with a top border and a shadow: it scrolls over the grid below it, and an
                    // unpainted bar would leave rows showing through the buttons.
                    backgroundColor: 'background.paper',
                    borderTop: 1,
                    borderColor: 'divider',
                    boxShadow: 3,
                    // Clears the AppBar (1100) without reaching Drawer/Modal. ag-grid's popups are lifted
                    // to 1150 globally in App.css, so an option list opening in the bottom band of a grid
                    // still draws over this.
                    zIndex: (theme) => theme.zIndex.appBar - 1,
                    mt: 2,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    // Right, unlike the scenario footer: on these pages the editable columns are the
                    // ones on the right of the row, so Save is where the cursor already is.
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
            >
                {/* Pinned to the far side of the bar, so it fills the empty middle without moving the
                    buttons. It yields to a blocking message, which lands in the same space and has to be
                    read: `justifyContent: flex-end` then leaves the pair exactly where it was. */}
                {legend && !firstIssue && <Box sx={{ mr: 'auto', pr: 2 }}>{legend}</Box>}
                {actions}
            </Box>
        );
    }

    // The footer goes the other way from the header — flex-start, at the page's left margin, under the
    // table's own left edge where the eye returns after reading a row (CALL-783) — and needs the gap that
    // keeps it off the bottom of the last grid. Right-aligned, its SPEICHERN sat directly under the row
    // grid's pinned trash column (measured x1336–1380 against the pair's x1169–1380), so the primary action
    // of the page shared a vertical with the one button that destroys a row.
    //
    // The testid is on the ROW rather than only on the button pair inside it: the alignment is what CALL-783
    // changed, and Testing Library forbids reaching it by walking up from the pair (no-node-access).
    return (
        <Box data-testid="scenario-actions-footer-row" sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
            {actions}
        </Box>
    );
};

export default FormSaveActions;
